import {
    priceUnitDisplay,
    type MeasurementSystem,
} from '@archipro-website/localisation';
import type CurrencyAmount from '../type/currency-amount';

/** The figure inside a ShortFormat such as "$1,234.50" or "-$12.00". */
const SHORT_FORMAT_NUMBER = /\d[\d,]*(?:\.\d+)?/;

const CENTS_PER_DOLLAR = 100;

/** Whole display units in one stored unit are shown to two decimals, e.g. 10.76 sq ft. */
const STEP_DECIMALS = 2;

/** Below a cent, a restated price keeps two significant figures so it never reads as $0.00. */
const SUB_CENT_SIGNIFICANT_DIGITS = 2;

/**
 * Cents, which may be fractional once restated per display unit, as the
 * figure of a ShortFormat: "4.65", or "0.0046" for a price under a cent.
 */
const formatMajorUnits = (cents: number): string => {
    const dollars = Math.abs(cents) / CENTS_PER_DOLLAR;
    if (dollars > 0 && dollars < 0.005) {
        return dollars.toLocaleString('en-US', {
            maximumSignificantDigits: SUB_CENT_SIGNIFICANT_DIGITS,
        });
    }
    return dollars.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/**
 * Display units in one stored unit, or null when the market shows the stored
 * unit itself (metric) or the price unit has no measurement (Box, Unit).
 */
const conversionFactor = (
    priceUnit: string | null | undefined,
    measurementSystem: MeasurementSystem,
): number | null => {
    const display = priceUnitDisplay(priceUnit, measurementSystem);
    if (!display || display.perStoredUnit === 1) {
        return null;
    }
    return display.perStoredUnit;
};

/**
 * A price set per square, linear or cubic metre restated per the market's
 * display unit (per sq ft, linear ft, cu ft on imperial markets).
 *
 * Display only: the cart and order stay in the stored metric unit, so what is
 * charged never changes. The amount stays in (possibly fractional) cents, so a
 * positive price never restates to zero and range bounds stay distinct; the
 * ShortFormat keeps the server's currency prefix.
 */
export const toDisplayUnitPrice = <T extends CurrencyAmount>(
    price: T,
    priceUnit: string | null | undefined,
    measurementSystem: MeasurementSystem,
): T => {
    const factor = conversionFactor(priceUnit, measurementSystem);
    if (!factor || !price.Amount) {
        return price;
    }

    const amount = price.Amount / factor;
    return {
        ...price,
        Amount: amount,
        ShortFormat: price.ShortFormat.replace(
            SHORT_FORMAT_NUMBER,
            formatMajorUnits(amount),
        ),
    };
};

/**
 * "sq ft", "lin ft" or "cu ft" when a price is restated in a unit other than
 * the stored one, so the figure needs its unit beside it. Null where the
 * market reads the stored unit (metric) or the price has no measurement, so
 * those prices show exactly as the server formats them.
 */
export const restatedPriceUnitLabel = (
    priceUnit: string | null | undefined,
    measurementSystem: MeasurementSystem,
): string | null =>
    conversionFactor(priceUnit, measurementSystem)
        ? (priceUnitDisplay(priceUnit, measurementSystem)?.shortLabel ?? null)
        : null;

/**
 * How much one quantity step buys, when the market's unit differs from the
 * stored one: a per-m² product on an imperial market is bought a square metre
 * at a time, so the shopper is told each step is 10.76 sq ft.
 */
export const quantityStepNote = (
    priceUnit: string | null | undefined,
    measurementSystem: MeasurementSystem,
): string | null => {
    const display = priceUnitDisplay(priceUnit, measurementSystem);
    if (!display || display.perStoredUnit === 1) {
        return null;
    }

    const step = Number(display.perStoredUnit.toFixed(STEP_DECIMALS));
    return `Sold in multiples of ${step} ${display.shortLabel}`;
};

/** The figure in a formatted price, divided by the unit factor and re-formatted. */
const convertPriceText = (text: string, factor: number): string =>
    text.replace(SHORT_FORMAT_NUMBER, (figure) =>
        formatMajorUnits(
            (Number(figure.replace(/,/g, '')) * CENTS_PER_DOLLAR) / factor,
        ),
    );

/**
 * The figure of a server-formatted price restated per display unit, without
 * the unit: for a struck-out price beside one labelled by
 * {@link toDisplayUnitPriceText}.
 */
export const toDisplayUnitFigure = (
    text: string,
    priceUnit: string | null | undefined,
    measurementSystem: MeasurementSystem,
): string => {
    const factor = conversionFactor(priceUnit, measurementSystem);
    return factor ? convertPriceText(text, factor) : text;
};

/**
 * A server-formatted price ("$50.00", as PricingDisplay.Text) as the market
 * reads it: restated per display unit and labelled with it, "$4.65 / sq ft".
 * Unchanged where the market reads the stored unit or the price has no
 * measurement (Box, Unit).
 */
export const toDisplayUnitPriceText = (
    text: string,
    priceUnit: string | null | undefined,
    measurementSystem: MeasurementSystem,
): string => {
    const unitLabel = restatedPriceUnitLabel(priceUnit, measurementSystem);
    if (!unitLabel) {
        return text;
    }

    return `${toDisplayUnitFigure(text, priceUnit, measurementSystem)} / ${unitLabel}`;
};

interface UnitPricedDisplay {
    Text?: string | null;
    ReplacedText?: string | null;
    ReferenceVariant?: { PriceUnit?: string | null } | null;
}

/**
 * A PricingDisplay with its texts as the market reads them: Text restated and
 * labelled per display unit, the struck-out ReplacedText restated to match.
 * Amounts are left as stored.
 */
export const toDisplayUnitPricing = <T extends UnitPricedDisplay>(
    pricing: T,
    measurementSystem: MeasurementSystem,
): T => {
    const priceUnit = pricing.ReferenceVariant?.PriceUnit;
    if (!restatedPriceUnitLabel(priceUnit, measurementSystem)) {
        return pricing;
    }

    return {
        ...pricing,
        Text: pricing.Text
            ? toDisplayUnitPriceText(pricing.Text, priceUnit, measurementSystem)
            : pricing.Text,
        ReplacedText: pricing.ReplacedText
            ? toDisplayUnitFigure(
                  pricing.ReplacedText,
                  priceUnit,
                  measurementSystem,
              )
            : pricing.ReplacedText,
    };
};
